import { useContext, useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { evaluateFormCondition } from '@ayumia/component-runtime';
import { text } from '../../app/i18n';
import { Dialog, DialogActions } from '../../design-system/Overlay';
import { Icon } from '../../design-system/icons';
import { EvidenceInput } from '../../views/EvidenceInput';
import { useReferenceLabels } from '../../views/reference-labels';
import { AppServicesContext, type AppServices } from '../../views/ViewContext';
import { civilShown, initials } from '../FormLayoutParts';
import { PadDateInput } from '../PadDateInput';
import { CivilTodayContext, ConformityRulesContext, ObjectSelector, SuggestedMark, TemplateEntry } from '../TemplateEntry';
import { FormReactionsContext } from '../form-reactions-context';
import { targetState, unitSymbol, type Answers, type Entry, type Inputs, type Section } from '../form-targets';
import { selectorCard, selectorOf, type SelectorRow } from '../object-selector';
import { stepperNext } from '../widgets/numeric';
import { Segmented } from './OptionTiles';
import { EchoName, PersonControl, personVariantOf } from './PersonField';
import { emptyText, numberShown, presentationOf } from './presentation';

/**
 * Bande de champs (rôle de région `strip`, layout-body 1.1.0 ; gabarit `core.layout.strip_list_detail`) :
 * une rangée de cellules compactes sous le bandeau, chacune avec son libellé en capitales et un contrôle
 * court — segmenté, bouton-sélecteur, bouton de date, compteur, bouton d'un champ à preuve (« Scanner », photo ;
 * `presentation.variant: button`, 1.10.0). Un bouton ouvre la saisie complète dans une fenêtre. Un autre widget garde son rendu.
 * Présentation seulement : le serveur relit la fiche.
 */

export interface CellProps {
  entry: Entry;
  values: Answers;
  inputs: Inputs;
  required: boolean;
  disabled: boolean;
  onChange: (value: unknown) => void;
  error: string | undefined;
  evidence: readonly string[];
  onEvidence: (ids: string[]) => void;
  subject: { type: string; id: string } | undefined;
}

const controlId = (entry: Entry): string => `input-${entry.field.field_id}`;

function ButtonLabel({ entry, shown, icon }: { entry: Entry; shown: string | undefined; icon: ReactNode }) {
  const empty = emptyText(entry);
  return (
    <>
      {icon}
      <span className={`ay-fv2-btnfield__text${shown === undefined ? ` ay-fv2-btnfield__text--${empty.tone}` : ''}`}>{shown ?? empty.text}</span>
    </>
  );
}

/** Bouton-sélecteur d'un champ `reference` : l'avatar de l'objet choisi (ou « ? »), son nom, la flèche. */
function SelectorButton(props: CellProps) {
  const services = useContext(AppServicesContext);
  // Sans services (aperçu hors application) : le bouton seul, sans liste.
  if (services === null) return <SelectorButtonView {...props} labels={new Map()} canOpen={false} />;
  return <SelectorButtonLive {...props} services={services} />;
}

function SelectorButtonLive({ services, ...props }: CellProps & { services: AppServices }) {
  const value = props.values[props.entry.field.field_id];
  const current = typeof value === 'string' && value !== '' ? value : undefined;
  const type = (props.entry.field as { reference_type?: string }).reference_type ?? '';
  const labels = useReferenceLabels(services.client, current === undefined ? [] : [{ type, ids: [current] }]) as ReadonlyMap<string, string>;
  // Sous-titre de l'objet choisi (« F1120 », numéro puis commune, comme la carte du sélecteur et le v1).
  const [subtitle, setSubtitle] = useState<string | undefined>(undefined);
  useEffect(() => {
    setSubtitle(undefined);
    if (current === undefined || type === '' || typeof services.client.getObject !== 'function') return undefined;
    let live = true;
    services.client.getObject(type, current).then(
      (read) => {
        const row = (read as { data?: SelectorRow }).data;
        if (live && row !== undefined && row !== null) setSubtitle(selectorCard(row, selectorOf(props.entry)).subtitle || undefined);
      },
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [services, type, current]);
  return <SelectorButtonView {...props} labels={labels} canOpen subtitle={subtitle} />;
}

function SelectorButtonView({ entry, values, required, disabled, onChange, error, labels, canOpen, subtitle }: CellProps & { labels: ReadonlyMap<string, string>; canOpen: boolean; subtitle?: string | undefined }) {
  const [open, setOpen] = useState(false);
  const id = entry.field.field_id;
  const value = values[id];
  const current = typeof value === 'string' && value !== '' ? value : undefined;
  const type = (entry.field as { reference_type?: string }).reference_type ?? '';
  const shown = current === undefined ? undefined : (labels.get(current) ?? 'Choix enregistré');
  const label = text(entry.field.label, 'fr', id);
  return (
    <>
      <button type="button" id={controlId(entry)} className="ay-fv2-btnfield ay-fv2-btnfield--selector" aria-haspopup="dialog" aria-invalid={error !== undefined} disabled={disabled} onClick={() => canOpen && setOpen(true)}>
        <span className="ay-fv2-btnfield__avatar" aria-hidden="true">
          {shown === undefined ? '?' : initials(shown) || '?'}
        </span>
        {shown !== undefined && subtitle !== undefined ? (
          <span className="ay-fv2-btnfield__names">
            <span className="ay-fv2-btnfield__text">{shown}</span>
            <span className="ay-fv2-btnfield__sub" data-testid={`${controlId(entry)}-sub`}>
              {subtitle}
            </span>
          </span>
        ) : (
          <ButtonLabel entry={entry} shown={shown} icon={null} />
        )}
        <Icon name="chevron-down" size={16} />
      </button>
      {open && (
        <Dialog title={label} open onClose={() => setOpen(false)} size="md">
          <ObjectSelector id={`${controlId(entry)}-list`} type={type} selector={selectorOf(entry)} label={label} value={value ?? null} onChange={onChange} disabled={disabled} invalid={false} describedBy={undefined} required={required} startOpen onClose={() => setOpen(false)} />
        </Dialog>
      )}
    </>
  );
}

const WEEKDAYS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];

/**
 * Date d'un bouton de la bande, comme le v1 (« lundi 19/10 ») : le jour de la semaine, puis JJ/MM ; l'année
 * s'ajoute quand elle n'est pas celle d'aujourd'hui (date civile du site). Même règle que la tablette (`stripDateText`).
 */
export function stripDateText(date: string | undefined, today: string | undefined): string | undefined {
  const match = date === undefined ? null : /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (match === null) return undefined;
  const weekday = WEEKDAYS[new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))).getUTCDay()]!;
  const sameYear = today !== undefined && today.slice(0, 4) === match[1];
  return `${weekday} ${match[3]}/${match[2]}${sameYear ? '' : `/${match[1]}`}`;
}

/** Bouton de date : l'icône du calendrier et la date (« lundi 19/10 »), ou le texte d'aide ; la saisie en fenêtre. */
function DateButton({ entry, values, required, disabled, onChange, error }: CellProps) {
  const today = useContext(CivilTodayContext);
  const [open, setOpen] = useState(false);
  const id = entry.field.field_id;
  const value = values[id];
  const shown = typeof value === 'string' && value !== '' ? (stripDateText(value, today ?? undefined) ?? civilShown(value)) : undefined;
  const quick = (entry.presentation as { quick_dates?: readonly string[] } | undefined)?.quick_dates ?? [];
  return (
    <>
      <button type="button" id={controlId(entry)} className="ay-fv2-btnfield ay-fv2-btnfield--date" aria-haspopup="dialog" aria-invalid={error !== undefined} disabled={disabled} onClick={() => setOpen(true)}>
        <ButtonLabel entry={entry} shown={shown} icon={<Icon name="calendar" size={18} />} />
      </button>
      {open && (
        <Dialog title={text(entry.field.label, 'fr', id)} open onClose={() => setOpen(false)} size="sm" footer={<DialogActions primary={{ label: 'OK', onClick: () => setOpen(false) }} />}>
          <PadDateInput id={`${controlId(entry)}-pad`} value={value ?? null} onChange={onChange} disabled={disabled} invalid={false} describedBy={undefined} required={required} today={today} quick={quick} />
        </Dialog>
      )}
    </>
  );
}

/** Compteur compact : « − », la valeur et son unité (couleur selon la cible si déclaré), « + ». */
export function CompactStepper({ entry, values, inputs, disabled, onChange, error }: CellProps) {
  const rules = useContext(ConformityRulesContext);
  const id = entry.field.field_id;
  const presentation = presentationOf(entry);
  const value = typeof values[id] === 'number' ? (values[id] as number) : null;
  const validation = entry.field.validation as { min?: number; max?: number } | undefined;
  const unit = (entry as { target?: { unit?: string } }).target?.unit ?? (entry.field as { unit?: string }).unit;
  const [typing, setTyping] = useState<string | null>(null);
  const move = (delta: 1 | -1): void => onChange(stepperNext(value, delta, presentation.step, validation?.min, validation?.max));
  const state = presentation.appearance?.target_colors === true ? targetState(entry, value, values, inputs, rules) : 'none';
  const tone = state === 'in' ? 'success' : state === 'out' || state === 'nonconform' ? 'danger' : state === 'wait' ? 'warning' : 'neutral';
  // Vide : « – – » comme le compteur du v1 (`Ck.compteur`) et la case du détail, au lieu d'un tiret seul.
  const empty = emptyText(entry).text;
  const shown = value === null ? (empty === '—' ? '– –' : empty) : numberShown(value, presentation.decimals);
  return (
    <span className="ay-fv2-stepper">
      <button type="button" className="ay-fv2-stepper__button" aria-label="Diminuer" aria-controls={controlId(entry)} disabled={disabled} onClick={() => move(-1)}>
        <Icon name="minus" size={18} />
      </button>
      <input
        id={controlId(entry)}
        className={`ay-fv2-stepper__value ay-fv2-stepper__value--${tone}`}
        type="text"
        inputMode="decimal"
        autoComplete="off"
        aria-invalid={error !== undefined}
        disabled={disabled}
        value={typing ?? (value === null ? shown : `${shown}${unit === undefined ? '' : ` ${unitSymbol(unit)}`}`)}
        onFocus={() => setTyping(value === null ? '' : numberShown(value, undefined))}
        onChange={(event) => setTyping(event.target.value.replace(/[^0-9,.-]/g, ''))}
        onBlur={() => {
          const typed = (typing ?? '').replace(',', '.');
          setTyping(null);
          if (typed === '') return;
          const number = Number(typed);
          if (Number.isFinite(number)) onChange(number);
        }}
        onKeyDown={(event) => {
          if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            setTyping(null);
            move(event.key === 'ArrowUp' ? 1 : -1);
          } else if (event.key === 'Enter') {
            event.preventDefault();
            (event.target as HTMLInputElement).blur();
          }
        }}
      />
      <button type="button" className="ay-fv2-stepper__button" aria-label="Augmenter" aria-controls={controlId(entry)} disabled={disabled} onClick={() => move(1)}>
        <Icon name="plus" size={18} />
      </button>
    </span>
  );
}

/**
 * Allure du bouton d'un champ à preuve : `scan_doc` (numériser un document) garde l'icône du scanner et
 * « Scanner » ; `photo` prend l'appareil photo et le libellé du champ ; un autre champ à preuve, l'icône de
 * sa nature (`evidence.kind`) et le libellé du champ. Un texte d'aide déclaré (`placeholder`) l'emporte.
 */
export function evidenceButtonLook(entry: Entry): { icon: 'scan' | 'camera' | 'signature' | 'file'; label: string } {
  const presentation = presentationOf(entry);
  const declared = presentation.placeholder as Readonly<Record<string, string>> | undefined;
  if (presentation.widget === 'scan_doc') return { icon: 'scan', label: text(declared, 'fr', 'Scanner') };
  const label = text(declared, 'fr', text(entry.field.label, 'fr', entry.field.field_id));
  if (presentation.widget === 'photo') return { icon: 'camera', label };
  const kind = entry.evidence?.kind;
  return { icon: kind === 'photo' ? 'camera' : kind === 'signature' ? 'signature' : 'file', label };
}

/** Bouton d'un champ à preuve (document, photo, signature) : l'icône, le libellé, le nombre de pièces jointes ; l'ajout en fenêtre. */
function EvidenceButton({ entry, disabled, evidence, onEvidence, subject, error }: CellProps) {
  const [open, setOpen] = useState(false);
  const label = text(entry.field.label, 'fr', entry.field.field_id);
  const look = evidenceButtonLook(entry);
  return (
    <>
      <button type="button" id={controlId(entry)} className="ay-fv2-btnfield ay-fv2-btnfield--action" aria-haspopup="dialog" aria-invalid={error !== undefined} disabled={disabled} onClick={() => setOpen(true)}>
        <Icon name={look.icon} size={18} />
        <span className="ay-fv2-btnfield__text">{look.label}</span>
        {evidence.length > 0 && <span className="ay-fv2-count">{evidence.length}</span>}
      </button>
      {open && (
        <Dialog title={label} open onClose={() => setOpen(false)} size="md" footer={<DialogActions primary={{ label: 'OK', onClick: () => setOpen(false) }} />}>
          <EvidenceInput id={`evidence-${entry.field.field_id}`} value={evidence} onChange={onEvidence} disabled={disabled} subject={subject} />
        </Dialog>
      )}
    </>
  );
}

/** Largeur déclarée d'une cellule (`presentation.layout.width`, colonnes sur 12) ; sinon à son contenu. */
function widthStyle(entry: Entry): CSSProperties | undefined {
  const width = presentationOf(entry).layout?.width;
  return typeof width === 'number' && width >= 1 && width <= 12 ? { flex: `0 0 calc(${width} / 12 * 100%)` } : undefined;
}

/**
 * La cellule prend la place d'un champ : un champ « Personne » en variante `field` (champ de sélection avec avatar)
 * s'élargit dans le bandeau, comme un champ de saisie, au lieu de suivre son contenu comme la puce.
 */
function fillsCell(entry: Entry): boolean {
  return presentationOf(entry).widget === 'person' && entry.field.type === 'reference' && personVariantOf(entry) === 'field';
}

/** Contrôle compact d'une cellule, ou `undefined` : le champ garde son rendu habituel. */
function compactControl(props: CellProps): ReactNode | undefined {
  const { entry, values, required, disabled, onChange } = props;
  const presentation = presentationOf(entry);
  const widget = presentation.widget;
  const type = entry.field.type;
  if (widget === 'segmented' && (type === 'enumeration' || type === 'boolean')) return <Segmented id={controlId(entry)} entry={entry} value={values[entry.field.field_id]} onChange={onChange} disabled={disabled} required={required} labelledBy={`${controlId(entry)}-label`} />;
  if (widget === 'stepper' && type === 'number') return <CompactStepper {...props} />;
  // Champ « Personne » (ADR-152) : puce avatar + nom + chevron dans le bandeau, ou champ, ou bouton.
  if (widget === 'person' && type === 'reference') return <PersonControl id={controlId(entry)} entry={entry} value={values[entry.field.field_id]} required={required} disabled={disabled} invalid={props.error !== undefined} onChange={onChange} labelledBy={`${controlId(entry)}-label`} />;
  if (presentation.variant !== 'button') return undefined;
  if (type === 'reference') return <SelectorButton {...props} />;
  if (type === 'date') return <DateButton {...props} />;
  if (entry.evidence !== undefined || widget === 'scan_doc' || widget === 'photo') return <EvidenceButton {...props} />;
  return undefined;
}

export function StripSection({
  section,
  answers,
  inputs,
  disabled,
  errors,
  setAnswer,
  evidence,
  setEvidence,
  subject,
}: {
  section: Section;
  answers: Answers;
  inputs: Inputs;
  disabled: boolean;
  errors: Readonly<Record<string, string>>;
  setAnswer: (id: string, value: unknown) => void;
  evidence: Readonly<Record<string, string[]>>;
  setEvidence: (id: string, ids: string[]) => void;
  subject: { type: string; id: string } | undefined;
}) {
  const shown = section.fields.filter((entry) => entry.compute === undefined && evaluateFormCondition(entry.visible_when, answers));
  const { origins } = useContext(FormReactionsContext);
  // Une rangée sans champ visible (« Collecte » en dépôt) ne laisse aucune trace.
  if (shown.length === 0) return null;
  return (
    <div className="ay-fv2-strip" role="group" aria-label={text(section.label, 'fr', section.section_id)} data-testid={`strip-${section.section_id}`}>
      {shown.map((entry) => {
        const id = entry.field.field_id;
        const required = entry.field.required === true || (entry.required_when !== undefined && evaluateFormCondition(entry.required_when, answers));
        const error = errors[`answers.${id}`] ?? errors[`evidence.${id}`];
        const props: CellProps = { entry, values: answers, inputs, required, disabled, onChange: (value) => setAnswer(id, value), error, evidence: evidence[id] ?? [], onEvidence: (ids) => setEvidence(id, ids), subject };
        const control = compactControl(props);
        if (control === undefined) {
          return (
            <div key={id} className="ay-fv2-strip__cell ay-fv2-strip__cell--plain" data-field={id}>
              <TemplateEntry entry={entry} values={answers} inputs={inputs} controlId={id} path={`answers.${id}`} required={required} disabled={disabled} onChange={props.onChange} {...(error === undefined ? {} : { error })} {...(subject === undefined ? {} : { subject })} />
            </div>
          );
        }
        return (
          <div key={id} className={`ay-fv2-strip__cell${fillsCell(entry) ? ' ay-fv2-strip__cell--fill' : ''}${error === undefined ? '' : ' ay-fv2-strip__cell--error'}`} data-field={id} style={widthStyle(entry)}>
            <span className="ay-fv2-strip__label" id={`${controlId(entry)}-label`}>
              {text(entry.field.label, 'fr', id)}
              {required && <span className="ay-visually-hidden"> (obligatoire)</span>}
            </span>
            <EchoName target={id} />
            {control}
            {origins[`answers.${id}`] !== undefined && <SuggestedMark controlId={id} />}
            {error !== undefined && <span className="ay-fv2-strip__error">{error}</span>}
          </div>
        );
      })}
    </div>
  );
}
